import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowClockwise, ChartLine, Warning } from 'phosphor-react';
import { PageHeader } from '../../../../components/PageHeader';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { GlassCard, GlassPageBg } from '../../../../components/glass';
import { IAlertaMetrica, IMetricaDia, useSocietario } from '../../../../hooks/useSocietario';

// Janelas de dias suportadas — match com o `Query(ge=1, le=365)` do backend.
const DIAS_OPTIONS: Array<{ label: string; value: number }> = [
  { label: '7 dias', value: 7 },
  { label: '30 dias', value: 30 },
  { label: '90 dias', value: 90 },
];

// `data` vem como ISO yyyy-mm-dd. Transforma em dd/mm pro eixo X dos charts.
const formatDateLabel = (iso: string): string => {
  const parts = iso.split('-');
  if (parts.length < 3) return iso;
  return `${parts[2]}/${parts[1]}`;
};

const formatUsd = (v: number): string => `$${v.toFixed(4)}`;
const formatUsdShort = (v: number): string => `$${v.toFixed(2)}`;
const formatSeconds = (v: number): string => `${v.toFixed(1)}s`;
const formatPercent = (v: number): string => `${v.toFixed(1)}%`;

interface ICustoChartPoint extends IMetricaDia {
  dateFormatted: string;
}

const ALERTA_COLOR: Record<string, string> = {
  custo_acima_threshold: 'red',
  taxa_critico_alta: 'orange',
};

const ALERTA_LABEL: Record<string, string> = {
  custo_acima_threshold: 'Custo acima do threshold',
  taxa_critico_alta: 'Taxa de críticos alta',
};

export const SocietarioAdminMetricsPage = () => {
  const { getMetrics } = useSocietario();
  const [dias, setDias] = useState<number>(30);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['societario', 'admin', 'metrics', dias],
    queryFn: () => getMetrics(dias),
    staleTime: 60_000,
  });

  // Pre-formata o eixo X uma vez — recharts re-renderiza varias vezes.
  const chartData: ICustoChartPoint[] = useMemo(
    () =>
      (data?.time_series ?? []).map(d => ({
        ...d,
        dateFormatted: formatDateLabel(d.data),
      })),
    [data?.time_series],
  );

  const agregados = data?.agregados;
  const alertas: IAlertaMetrica[] = data?.alertas ?? [];
  const temDados = chartData.length > 0;

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" px={{ base: 4, md: 6 }} py={6}>
        <ReturnButton previousPage="societario" label="Voltar" />
        <PageHeader
          title="Societário — Métricas v2"
          subtitle="Custo, latência e qualidade da geração com IA. Atualizado por agregação diária."
        />

        <Flex justify="space-between" align="center" mt={4} mb={4} gap={3} flexWrap="wrap">
          <ButtonGroup size="sm" isAttached variant="outline">
            {DIAS_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                colorScheme={dias === opt.value ? 'blue' : 'gray'}
                variant={dias === opt.value ? 'solid' : 'outline'}
                onClick={() => setDias(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<ArrowClockwise size={14} />}
            onClick={() => refetch()}
            isLoading={isFetching}
          >
            Atualizar
          </Button>
        </Flex>

        {isLoading ? (
          <Flex align="center" gap={2} py={6}>
            <Spinner size="sm" />
            <Text fontSize="sm" color="gray.600">
              Carregando métricas…
            </Text>
          </Flex>
        ) : null}

        {isError ? (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <Text fontSize="sm">Falha ao carregar métricas.</Text>
              <Button size="xs" mt={2} onClick={() => refetch()} isLoading={isFetching}>
                Tentar novamente
              </Button>
            </Box>
          </Alert>
        ) : null}

        {data && !isLoading ? (
          <>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4} mb={4}>
              <GlassCard intensity="strong" p={4}>
                <Stat>
                  <StatLabel>Contratos gerados</StatLabel>
                  <StatNumber>{agregados?.contratos_total ?? 0}</StatNumber>
                  <StatHelpText>
                    {agregados?.dias ?? 0} {agregados?.dias === 1 ? 'dia' : 'dias'} com agregação
                  </StatHelpText>
                </Stat>
              </GlassCard>
              <GlassCard intensity="strong" p={4}>
                <Stat>
                  <StatLabel>Custo total</StatLabel>
                  <StatNumber>{formatUsdShort(agregados?.custo_total_usd ?? 0)}</StatNumber>
                  <StatHelpText>USD na janela</StatHelpText>
                </Stat>
              </GlassCard>
              <GlassCard intensity="strong" p={4}>
                <Stat>
                  <StatLabel>Custo médio / contrato</StatLabel>
                  <StatNumber>{formatUsd(agregados?.custo_medio_periodo_usd ?? 0)}</StatNumber>
                  <StatHelpText>Média ponderada por geração</StatHelpText>
                </Stat>
              </GlassCard>
              <GlassCard intensity="strong" p={4}>
                <Stat>
                  <StatLabel>Latência P95 (pico)</StatLabel>
                  <StatNumber>{formatSeconds(agregados?.latencia_p95_max_s ?? 0)}</StatNumber>
                  <StatHelpText>Maior P95 diário no período</StatHelpText>
                </Stat>
              </GlassCard>
            </SimpleGrid>

            {alertas.length > 0 ? (
              <GlassCard intensity="strong" mb={4} borderColor="rgba(221, 107, 32, 0.4)">
                <HStack p={3} borderBottomWidth="1px" borderColor="orange.100" spacing={2}>
                  <Warning size={20} color="#DD6B20" weight="duotone" />
                  <Text fontWeight="bold" fontSize="md">
                    Alertas ({alertas.length})
                  </Text>
                </HStack>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Data</Th>
                      <Th>Tipo</Th>
                      <Th isNumeric>Valor</Th>
                      <Th isNumeric>Threshold</Th>
                      <Th>Mensagem</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {alertas.map(a => (
                      <Tr key={`${a.data}-${a.tipo}`}>
                        <Td>{formatDateLabel(a.data)}</Td>
                        <Td>
                          <Badge colorScheme={ALERTA_COLOR[a.tipo] ?? 'gray'} fontSize="2xs">
                            {ALERTA_LABEL[a.tipo] ?? a.tipo}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          {a.tipo === 'custo_acima_threshold'
                            ? formatUsd(a.valor)
                            : formatPercent(a.valor)}
                        </Td>
                        <Td isNumeric>
                          {a.tipo === 'custo_acima_threshold'
                            ? formatUsdShort(a.threshold)
                            : formatPercent(a.threshold)}
                        </Td>
                        <Td fontSize="xs" color="gray.600">
                          {a.mensagem}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </GlassCard>
            ) : null}

            {!temDados ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Sem agregação de métricas neste período. As métricas são populadas conforme
                  contratos v2 são gerados.
                </Text>
              </Alert>
            ) : (
              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
                <ChartCard
                  title="Custo médio diário (USD)"
                  subtitle="Custo médio por contrato gerado no dia"
                >
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dateFormatted" fontSize={10} />
                      <YAxis fontSize={10} tickFormatter={v => `$${(v as number).toFixed(3)}`} />
                      <Tooltip formatter={(v: number) => formatUsd(v)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="custo_medio_usd"
                        stroke="#7C3AED"
                        name="Custo médio (USD)"
                        dot={false}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Latência (s)" subtitle="P50 vs P95 por dia">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dateFormatted" fontSize={10} />
                      <YAxis fontSize={10} tickFormatter={v => `${(v as number).toFixed(0)}s`} />
                      <Tooltip formatter={(v: number) => formatSeconds(v)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="latencia_p50_s"
                        stroke="#4299E1"
                        name="P50"
                        dot={false}
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="latencia_p95_s"
                        stroke="#FC8181"
                        name="P95"
                        dot={false}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard
                  title="Taxa de issues críticos (%)"
                  subtitle="Quanto maior, mais regressão na qualidade"
                >
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dateFormatted" fontSize={10} />
                      <YAxis fontSize={10} tickFormatter={v => `${(v as number).toFixed(0)}%`} />
                      <Tooltip formatter={(v: number) => formatPercent(v)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="taxa_issues_criticos"
                        stroke="#E53E3E"
                        name="% críticos"
                        dot={false}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard
                  title="Taxa de regeneração (%)"
                  subtitle="Contratos regerados / total — alto = IA acertando pouco de primeira"
                >
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dateFormatted" fontSize={10} />
                      <YAxis fontSize={10} tickFormatter={v => `${(v as number).toFixed(0)}%`} />
                      <Tooltip formatter={(v: number) => formatPercent(v)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="taxa_regeneracao"
                        stroke="#38A169"
                        name="% regerados"
                        dot={false}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </SimpleGrid>
            )}
          </>
        ) : null}
      </Box>
    </GlassPageBg>
  );
};

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const ChartCard = ({ title, subtitle, children }: ChartCardProps) => (
  <GlassCard intensity="strong" p={4}>
    <VStack align="stretch" spacing={1} mb={3}>
      <HStack spacing={2}>
        <ChartLine size={18} color="#4A5568" weight="duotone" />
        <Text fontWeight="bold" fontSize="md">
          {title}
        </Text>
      </HStack>
      {subtitle ? (
        <Text fontSize="xs" color="gray.500">
          {subtitle}
        </Text>
      ) : null}
    </VStack>
    {children}
  </GlassCard>
);

export default SocietarioAdminMetricsPage;
